import React from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCard = ({ product, onWishlistToggle }) => {
  const { isAuthenticated } = useAuth();

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please login to add to wishlist');
      return;
    }

    try {
      await axios.post(`${API}/wishlist/add`, { product_id: product.id });
      toast.success('Added to wishlist');
      if (onWishlistToggle) onWishlistToggle();
    } catch (error) {
      toast.error('Failed to add to wishlist');
    }
  };

  return (
    <Link 
      to={`/products/${product.id}`}
      className="group relative bg-white border border-gray-200 overflow-hidden hover:border-black transition-colors duration-300 product-card"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="aspect-square overflow-hidden bg-gray-100">
        <img
          src={product.images?.[0] || 'https://via.placeholder.com/400'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* Wishlist Button */}
      <button
        onClick={handleWishlist}
        className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-chicago-red hover:text-white"
        data-testid={`wishlist-btn-${product.id}`}
      >
        <Heart className="w-5 h-5" />
      </button>

      {/* Badges */}
      <div className="absolute top-4 left-4 flex flex-col gap-2">
        {product.featured && (
          <span className="bg-chicago-red text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
            Featured
          </span>
        )}
        {product.condition === 'Deadstock' && (
          <span className="bg-deep-obsidian text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
            DS
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <p className="text-cement-grey text-xs uppercase tracking-wider font-medium">{product.brand}</p>
            <h3 className="font-chivo font-bold text-deep-obsidian line-clamp-2">{product.name}</h3>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-baseline gap-2">
            <span className="font-bebas text-2xl text-deep-obsidian">${product.price.toFixed(2)}</span>
            {product.original_price && (
              <span className="text-cement-grey text-sm line-through">${product.original_price.toFixed(2)}</span>
            )}
          </div>
          <span className="text-xs text-cement-grey uppercase tracking-wider">{product.era}</span>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-cement-grey">{product.condition}</span>
          <span className="text-xs text-cement-grey">{product.size.length} sizes</span>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
